import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { requestPasswordReset } from '../../services/loyaltyAuthApi';
import { FONT_FAMILY } from '../../theme';

export default function ForgotPassword() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        if (!email) {
            setError(t('loyalty.auth.errors.emailRequired') || 'Email is required');
            return;
        }

        setLoading(true);

        try {
            await requestPasswordReset(email);
            setSuccess(true);
            setEmail('');
        } catch (err) {
            if (err.errors && err.errors.email) {
                const errorMsg = err.errors.email[0];
                // Translate common backend error messages
                if (errorMsg.includes('required')) {
                    setError(t('loyalty.forgotPassword.errors.emailRequired') || errorMsg);
                } else if (errorMsg.includes('valid email')) {
                    setError(t('loyalty.forgotPassword.errors.invalidEmail') || errorMsg);
                } else if (errorMsg.includes('could not find')) {
                    setError(t('loyalty.forgotPassword.errors.userNotFound') || errorMsg);
                } else {
                    setError(errorMsg);
                }
            } else {
                const errorMsg = err.message || '';
                // Translate backend error messages
                if (errorMsg.includes('could not find')) {
                    setError(t('loyalty.forgotPassword.errors.userNotFound') || errorMsg);
                } else {
                    setError(errorMsg || t('loyalty.auth.errors.genericError') || 'An error occurred');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#1a1a1a',
                backgroundImage: 'url(/images/gallery-images/20231106_183210-scaled.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.75)',
                    backdropFilter: 'blur(3px)',
                },
            }}
        >
            <Card
                sx={{
                    maxWidth: 480,
                    width: '100%',
                    mx: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.98)',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                    borderRadius: 3,
                    position: 'relative',
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                    <Button
                        component={Link}
                        to="/loyalty/login"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            mb: 2,
                            color: '#666',
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                    >
                        {t('loyalty.forgotPassword.backToLogin') || 'Back to Login'}
                    </Button>

                    <Typography
                        variant="h4"
                        sx={{
                            mb: 1,
                            fontWeight: 700,
                            fontFamily: FONT_FAMILY,
                            color: '#111',
                            textAlign: 'center',
                        }}
                    >
                        {t('loyalty.forgotPassword.title') || 'Forgot Password'}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mb: 4,
                            color: '#666',
                            textAlign: 'center',
                            lineHeight: 1.6,
                        }}
                    >
                        {t('loyalty.forgotPassword.description') || 'Enter your email address and we\'ll send you a link to reset your password.'}
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            {t('loyalty.forgotPassword.successMessage') || 'If an account exists with that email, you will receive a password reset link shortly. Please check your inbox.'}
                        </Alert>
                    )}

                    {!success && (
                        <form onSubmit={handleSubmit}>
                            <TextField
                                label={t('loyalty.auth.email') || 'Email'}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                required
                                disabled={loading}
                                sx={{ mb: 3 }}
                                autoFocus
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    bgcolor: '#ed5034',
                                    color: '#fff',
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: '#d64528' },
                                    '&:disabled': { bgcolor: '#ccc' },
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                                ) : (
                                    t('loyalty.forgotPassword.sendButton') || 'Send Reset Link'
                                )}
                            </Button>
                        </form>
                    )}

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            {t('loyalty.forgotPassword.rememberPassword') || 'Remember your password?'}{' '}
                            <Link
                                to="/loyalty/login"
                                style={{
                                    color: '#ed5034',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                {t('loyalty.auth.login') || 'Login'}
                            </Link>
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
